import { NULL_TAG } from '@racemap/utilities/consts/events';
import { Space } from 'antd';
import classnames from 'classnames';
import { useRef, useState } from 'react';
import { IconCaretDown, IconCaretUp, IconClose } from 'src/components/Icon';
import { t } from 'src/lib/player/i18n';
import { useClickOutside } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';

interface MultiSelectProps {
  tagKey: string;
  values: Array<[string, number]>;
  selectedValues: Array<string> | undefined;
  onToggle: (tagKey: string, value: string) => void;
}

export default function MultiSelect({
  tagKey,
  values,
  selectedValues,
  onToggle,
}: MultiSelectProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    if (isOpen) setIsOpen(false);
  });
  function renderValueLabel(value: string) {
    return value === NULL_TAG ? (
      <span className="tag-multiselect-none">{t('tags.none')}</span>
    ) : (
      value
    );
  }

  return (
    <div className="tag-multiselect">
      <div className="tag-multiselect-key">{t(`tags.${tagKey}`, tagKey)}</div>
      <div
        className="tag-multiselect-values"
        ref={ref}
        {...onClickGenerator(() => {
          setIsOpen(true);
        })}
      >
        <div className="tag-multiselect-input" style={{ zIndex: isOpen ? 20 : 1 }}>
          <Space>
            {selectedValues != null && selectedValues.length > 0
              ? selectedValues.map((value) => (
                  <div
                    {...onClickGenerator((ev) => {
                      ev.stopPropagation();
                      onToggle(tagKey, value);
                    })}
                    key={value}
                    className={classnames('tag-multiselect-item', 'selected')}
                  >
                    {renderValueLabel(value)} &times;
                  </div>
                ))
              : !isOpen &&
                values.slice(0, 20).map(([value]) => (
                  <div
                    key={value}
                    className={classnames('tag-multiselect-item', 'preview')}
                    {...onClickGenerator((ev) => {
                      ev.stopPropagation();
                      setIsOpen(true);
                    })}
                  >
                    {renderValueLabel(value)}
                  </div>
                ))}
          </Space>
          <Space style={{ marginRight: 5 }}>
            {selectedValues != null && selectedValues.length > 0 && (
              <IconClose
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  for (const value of selectedValues) {
                    onToggle(tagKey, value);
                  }
                })}
              />
            )}
            {isOpen ? (
              <IconCaretUp
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                })}
              />
            ) : (
              <IconCaretDown
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                })}
              />
            )}
          </Space>
        </div>
        {isOpen && (
          <div className="tag-multiselect-scroller">
            {values.map(([value, count]) => (
              <div
                key={value}
                className={classnames('tag-multiselect-item', {
                  selected: selectedValues?.includes(value),
                })}
                {...onClickGenerator((ev) => {
                  ev.stopPropagation();
                  onToggle(tagKey, value);
                })}
              >
                {renderValueLabel(value)}{' '}
                <span className="tag-multiselect-item-count">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
